import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import { useSaleManager } from '../../../../../../ng-compat/hooks'
import {
  selectSaleFulfilmentDetail,
  setFulfilmentDetails,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { openSaleAddressModal, closeSaleAddressModal } from '../../modals'
import { FulfilmentDetail } from '../../../../../../models/fulfilment'
import type { AddOrEditAddressButtonType } from '../EditAddressButton/EditAddressButton.type'

interface UseAddressModalHandlersParams {
  type: AddOrEditAddressButtonType
  fulfilmentDetail?: FulfilmentDetail
  onClose?: () => void
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

interface UseAddressModalHandlersReturn {
  openAddressModal: () => void
}

/**
 * Hook that provides Web Register-specific handlers for the address modal.
 * This encapsulates Redux dispatch, SaleManager calls, and modal system integration.
 *
 * Used by AddOrEditAddressButton to handle opening/closing the address modal
 * and saving fulfillment details to the sale.
 *
 * @param params - Configuration for the modal handlers
 * @returns Object containing the openAddressModal function
 *
 * @example
 * ```tsx
 * const { openAddressModal } = useAddressModalHandlers({
 *   type: 'edit',
 *   onSave: handleSave,
 *   onClose: handleClose,
 * })
 * ```
 */
export const useAddressModalHandlers = ({
  type,
  fulfilmentDetail,
  onClose,
  onSave,
}: UseAddressModalHandlersParams): UseAddressModalHandlersReturn => {
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const savedFulfilmentDetails = useAppSelector(selectSaleFulfilmentDetail)
  const fulfilmentDetails = fulfilmentDetail ?? savedFulfilmentDetails

  const closeAddressModal = useCallback(
    (fulfilmentDetails?: FulfilmentDetail) => {
      if (fulfilmentDetails && !onSave) {
        dispatch(
          setFulfilmentDetails([
            { ...fulfilmentDetails!, shouldAddNewAddress: false },
          ])
        )
      }
      closeSaleAddressModal()
      onClose?.()
    },
    [dispatch, onClose, onSave]
  )

  const onSaveForm = useCallback(
    (fulfilmentDetails: FulfilmentDetail[]) => {
      if (onSave) {
        onSave(fulfilmentDetails)
        return
      }

      dispatch(setFulfilmentDetails(fulfilmentDetails))
      saleManager.updateFulfillmentDetails(fulfilmentDetails)
    },
    [dispatch, onSave, saleManager]
  )

  const openAddressModal = useCallback(() => {
    onClose?.()
    openSaleAddressModal({
      formType: type,
      fulfilmentDetails,
      onClose: closeAddressModal,
      onSave: onSaveForm,
    })
  }, [closeAddressModal, fulfilmentDetails, onClose, onSaveForm, type])

  return {
    openAddressModal,
  }
}
